// Shared status configurations and workflow color mappings
// Single source of truth for all status badges, plan statuses, and workflow colors

// Transaction status config (completed, pending, waiting_approval)
export const STATUS_CFG = {
  completed: { ar: 'مكتملة', en: 'Completed', bg: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending: { ar: 'قيد الإجراء', en: 'In Progress', bg: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  waiting_approval: { ar: 'بانتظار موافقة السيستم', en: 'Awaiting Approval', bg: 'bg-blue-500', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  cancelled: { ar: 'ملغي', en: 'Cancelled', bg: 'bg-red-400', badge: 'bg-red-50 text-red-600 border-red-200' },
};

// Work plan step status config (planned, done_unpaid, executing, completed, cancelled)
export const PLAN_STATUS = {
  planned: { ar: 'مخطط', en: 'Planned', bg: 'bg-slate-200', badge: 'bg-slate-100 text-slate-600 border-slate-200' },
  done_unpaid: { ar: 'تم - غير مدفوع', en: 'Done - Unpaid', bg: 'bg-purple-500', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  executing: { ar: 'قيد التنفيذ', en: 'Executing', bg: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  awaiting_approval: { ar: 'بانتظار الموافقة', en: 'Awaiting Approval', bg: 'bg-blue-500', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  completed: { ar: 'مكتمل', en: 'Completed', bg: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cancelled: { ar: 'ملغي', en: 'Cancelled', bg: 'bg-red-400', badge: 'bg-red-50 text-red-600 border-red-200' },
};

// Work plan payment status config
export const PAYMENT_STATUS = {
  paid: { ar: 'مدفوعة', en: 'Paid', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  unpaid: { ar: 'غير مدفوعة', en: 'Unpaid', badge: 'bg-red-50 text-red-600 border-red-200' },
  on_account: { ar: 'على الحساب', en: 'On Account', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  cancelled: { ar: 'ملغية', en: 'Cancelled', badge: 'bg-red-50 text-red-600 border-red-200' },
};

// Workflow type colors - used in Tracking page (rows, bars, badges)
export const WF_COLORS = {
  new_residence: { bg: 'bg-emerald-200/60', text: 'text-emerald-700', border: 'border-emerald-300', bar: 'bg-emerald-500' },
  renewal: { bg: 'bg-blue-200/60', text: 'text-blue-700', border: 'border-blue-300', bar: 'bg-blue-500' },
  temp_work_permit: { bg: 'bg-violet-200/60', text: 'text-violet-700', border: 'border-violet-300', bar: 'bg-violet-500' },
  partial_work: { bg: 'bg-violet-200/60', text: 'text-violet-700', border: 'border-violet-300', bar: 'bg-violet-500' },
  cancellation: { bg: 'bg-red-200/60', text: 'text-red-700', border: 'border-red-300', bar: 'bg-red-500' },
  cancellation_temp_6m: { bg: 'bg-orange-200/60', text: 'text-orange-700', border: 'border-orange-300', bar: 'bg-orange-500' },
  new_residence_outside: { bg: 'bg-teal-200/60', text: 'text-teal-700', border: 'border-teal-300', bar: 'bg-teal-500' },
  cancellation_abroad: { bg: 'bg-rose-200/60', text: 'text-rose-700', border: 'border-rose-300', bar: 'bg-rose-500' },
  cancellation_temp_2y: { bg: 'bg-amber-200/60', text: 'text-amber-700', border: 'border-amber-300', bar: 'bg-amber-500' },
  gcc_work_permit: { bg: 'bg-lime-200/60', text: 'text-lime-700', border: 'border-lime-300', bar: 'bg-lime-500' },
  new_establishment: { bg: 'bg-indigo-200/60', text: 'text-indigo-700', border: 'border-indigo-300', bar: 'bg-indigo-500' },
  establishment_renewal: { bg: 'bg-cyan-200/60', text: 'text-cyan-700', border: 'border-cyan-300', bar: 'bg-cyan-500' },
  establishment_transactions: { bg: 'bg-indigo-200/60', text: 'text-indigo-700', border: 'border-indigo-300', bar: 'bg-indigo-500' },
};

const WF_COLORS_DEFAULT = { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', bar: 'bg-slate-400' };

export const getWfColor = (mainType) => WF_COLORS[mainType] || WF_COLORS_DEFAULT;

// Workflow type colors for CreatePlan (lighter variant for picker UI)
export const WF_PICKER_COLORS = {
  new_residence: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'bg-emerald-500', dark: 'bg-emerald-900' },
  renewal: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'bg-blue-500', dark: 'bg-blue-900' },
  temp_work_permit: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', icon: 'bg-violet-500', dark: 'bg-violet-900' },
  partial_work: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', icon: 'bg-violet-500', dark: 'bg-violet-900' },
  gcc_work_permit: { bg: 'bg-lime-50', border: 'border-lime-200', text: 'text-lime-700', icon: 'bg-lime-500', dark: 'bg-lime-900' },
  cancellation: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'bg-red-500', dark: 'bg-red-900' },
  cancellation_temp_6m: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: 'bg-orange-500', dark: 'bg-orange-900' },
  new_residence_outside: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', icon: 'bg-teal-500', dark: 'bg-teal-900' },
  cancellation_abroad: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', icon: 'bg-rose-500', dark: 'bg-rose-900' },
  cancellation_temp_2y: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'bg-amber-500', dark: 'bg-amber-900' },
  new_establishment: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', icon: 'bg-indigo-500', dark: 'bg-indigo-900' },
  establishment_renewal: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', icon: 'bg-cyan-500', dark: 'bg-cyan-900' },
  establishment_transactions: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', icon: 'bg-indigo-500', dark: 'bg-indigo-900' },
};

const WF_PICKER_DEFAULT = { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', icon: 'bg-slate-500', dark: 'bg-slate-800' };

export const getWfPickerColor = (mainType) => WF_PICKER_COLORS[mainType] || WF_PICKER_DEFAULT;

// Status dot colors (for mini progress indicators)
export const STATUS_DOT = {
  completed: 'bg-emerald-500',
  in_progress: 'bg-amber-500',
  waiting_approval: 'bg-blue-500',
  upcoming: 'bg-slate-200',
  cancelled: 'bg-red-400',
  pending: 'bg-amber-500',
};

// Plan step dot colors
export const PLAN_DOT = {
  planned: 'bg-slate-300',
  done_unpaid: 'bg-purple-500',
  executing: 'bg-amber-500',
  awaiting_approval: 'bg-blue-500',
  completed: 'bg-emerald-500',
  cancelled: 'bg-red-400',
};
